import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { emailApi } from "@/services/api";
import { Mail, Send, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { formatDate } from "@/lib/utils";

interface EmailConfig {
  id: string;
  provider: string;
  awsRegion?: string;
  awsAccessKeyId?: string;
  fromEmail: string;
  fromName?: string;
  replyTo?: string;
  isActive: boolean;
  lastTestedAt?: string;
  lastTestStatus?: string;
}

export function EmailSettingsPage() {
  const [config, setConfig] = useState<EmailConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [useInviteTemplate, setUseInviteTemplate] = useState(true);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    provider: "ses",
    awsRegion: "",
    awsAccessKeyId: "",
    awsSecretAccessKey: "",
    fromEmail: "",
    fromName: "",
    replyTo: "",
  });

  useEffect(() => {
    loadConfig();
  }, []);

  async function loadConfig() {
    try {
      const data = await emailApi.getConfig();
      if (data) {
        setConfig(data);
        setFormData({
          provider: data.provider || "ses",
          awsRegion: data.awsRegion || "",
          awsAccessKeyId: data.awsAccessKeyId || "",
          awsSecretAccessKey: "",
          fromEmail: data.fromEmail || "",
          fromName: data.fromName || "",
          replyTo: data.replyTo || "",
        });
      }
    } catch (error) {
      console.error("Erro ao carregar config:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    try {
      const dataToSave = { ...formData };
      if (!dataToSave.awsSecretAccessKey) {
        delete (dataToSave as Record<string, unknown>).awsSecretAccessKey;
      }

      const result = await emailApi.updateConfig(dataToSave);
      setConfig(result);
      setTestResult({ success: true, message: "Configuracao salva com sucesso!" });
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao salvar",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    if (!testEmail) return;

    setTesting(true);
    setTestResult(null);
    try {
      const result = await emailApi.test(testEmail, useInviteTemplate);
      setTestResult(result);
      await loadConfig(); // Reload to get updated test status
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro no teste",
      });
    } finally {
      setTesting(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<Mail className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Configuração de Email"
          description="AWS SES para envio de emails"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<Mail className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Configuração de Email"
        description="AWS SES para envio de emails"
        stats={
          config
            ? [
                {
                  value: config.lastTestStatus === "success" ? "✓" : "—",
                  label: "Status",
                },
              ]
            : undefined
        }
      />

      {/* Status Card */}
      {config && (
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-medium">AWS SES</p>
                  <p className="text-sm text-muted-foreground">
                    {config.fromEmail}
                  </p>
                </div>
              </div>
              <Badge
                variant={
                  config.lastTestStatus === "success" ? "success" : "secondary"
                }
              >
                {config.lastTestStatus === "success"
                  ? "Configurado"
                  : "Não testado"}
              </Badge>
            </div>
            {config.lastTestedAt && (
              <p className="mt-3 text-sm text-muted-foreground">
                Último teste: {formatDate(config.lastTestedAt)}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* AWS SES Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">AWS SES</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Região AWS</label>
              <Input
                placeholder="us-east-1"
                value={formData.awsRegion}
                onChange={(e) =>
                  setFormData({ ...formData, awsRegion: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium">Access Key ID</label>
              <Input
                placeholder="AKIA..."
                value={formData.awsAccessKeyId}
                onChange={(e) =>
                  setFormData({ ...formData, awsAccessKeyId: e.target.value })
                }
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Secret Access Key</label>
            <Input
              type="password"
              placeholder={config ? "Deixe vazio para manter atual" : "Secret Key"}
              value={formData.awsSecretAccessKey}
              onChange={(e) =>
                setFormData({ ...formData, awsSecretAccessKey: e.target.value })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* General Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configurações de Envio</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium">Email de Envio</label>
            <Input
              type="email"
              placeholder="noreply@stablebrld.com"
              value={formData.fromEmail}
              onChange={(e) =>
                setFormData({ ...formData, fromEmail: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Nome do Remetente</label>
              <Input
                placeholder="StableBRLD"
                value={formData.fromName}
                onChange={(e) =>
                  setFormData({ ...formData, fromName: e.target.value })
                }
              />
            </div>
            <div>
              <label className="text-sm font-medium">Reply-To</label>
              <Input
                type="email"
                placeholder="suporte@stablebrld.com"
                value={formData.replyTo}
                onChange={(e) =>
                  setFormData({ ...formData, replyTo: e.target.value })
                }
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Test Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Testar Configuração</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="seu@email.com"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="flex-1"
            />
            <Button onClick={handleTest} disabled={testing || !testEmail}>
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Enviar Teste
            </Button>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={useInviteTemplate}
              onChange={(e) => setUseInviteTemplate(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <div>
              <span className="text-sm font-medium">Usar template de convite</span>
              <p className="text-xs text-muted-foreground">
                Envia o email com o design completo de convite de usuário
              </p>
            </div>
          </label>

          {testResult && (
            <div
              className={`p-4 rounded-lg flex items-center gap-3 ${
                testResult.success
                  ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              {testResult.success ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <XCircle className="w-5 h-5" />
              )}
              <span>{testResult.message}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Salvar Configuração
        </Button>
      </div>
    </div>
  );
}
